'use client';

import React, { useState } from 'react';
import { Button, Checkbox } from 'antd';
import Image from 'next/image';

import bindLogo from '../../../public/assets/bind-logo.png';
import Highlight from '@/components/ui/Highlight';
import { notificactionWarning } from '@/components/ui/notificaction';
import { armarBloquesEmpresa, VistaPrevia } from '@/utils/services/vistaPreviaRL';

import classes from './onboardingDefault.module.scss';
import propios from './onboardingRepresentanteLegal.module.scss';

interface Props {
  logo: string;
  titulo: React.ReactNode;
  vistaPrevia: VistaPrevia | null;
  onIniciar: () => Promise<void>;
  openTyC: () => void;
}

/**
 * Pantalla de inicio del representante legal de una persona juridica (RF-ONB-19, AB#10741/US-7).
 *
 * Hasta ahora el representante entraba por la bienvenida generica, que no le decia nada de la
 * empresa que iba a representar y le pedia aceptar terminos sin mostrarle sobre que. Aca ve los
 * datos que el operador cargo y acepta terminos y datos antes de arrancar.
 *
 * Los datos van en bloques separados desde AB#11306. Estaban condensados en un parrafo unico, y lo
 * que se le pide al representante es ratificarlos de a uno: en texto corrido no se pueden leer.
 *
 * ALCANCE: es una bienvenida, no la conformidad (AB#10817). Elegir cual de los representantes
 * declarados es quien completa, y el registro de esa conformidad con autoria y fecha, viven en
 * /conformidad-rl, que es el paso creado para eso. Tenerlo en los dos lados obligaba a mantener dos
 * versiones del mismo acto.
 *
 * Es un componente aparte y no un bloque agregado a OnboardingDefault/OnboardingVirginia porque
 * esas dos son copias casi identicas entre si: meter el parrafo en ambas garantiza que se
 * desincronicen. El saludo llega por prop para que cada entidad conserve el suyo.
 */
const OnboardingRepresentanteLegal: React.FC<Props> = ({ logo, titulo, vistaPrevia, onIniciar, openTyC }) => {
  const [aceptaTerminos, setAceptaTerminos] = useState(false);
  const [aceptaDatos, setAceptaDatos] = useState(false);
  const [enviando, setEnviando] = useState(false);

  const handleSubmit = async () => {
    if (!aceptaTerminos || !aceptaDatos) {
      notificactionWarning('Para continuar tenés que aceptar los términos y confirmar que los datos son correctos');
      return;
    }

    // Bloqueo mientras avanza: sin esto un doble click dispara dos veces el paso siguiente.
    setEnviando(true);
    try {
      await onIniciar();
    } finally {
      setEnviando(false);
    }
  };

  const bloques = armarBloquesEmpresa(vistaPrevia);

  return (
    <div className={`${classes.container} ${propios.contenedor}`}>
      <div className={classes.logoContainer}>
        {/* AB#11070: logo puede llegar null/vacio; next/image explota con src null. */}
        <Image src={logo || bindLogo} alt="Logo" width={500} height={500} className={classes.logo} />
      </div>

      <h1 className={classes.mainTitle}>{titulo}</h1>

      <p className={classes.description}>{bloques.encabezado}</p>

      {bloques.empresa && (
        <div className={propios.datosEmpresa}>
          {/* Los datos de la empresa van en una sola linea (razon social - CUIT - domicilio -
              telefono). La version con cada dato en su fila quedaba demasiado aireada para lo que
              es: el encabezado de lo que el representante esta por ratificar. */}
          <p className={propios.empresa}>
            <Highlight>{bloques.empresa.razonSocial}</Highlight>
            {bloques.empresa.detalle && ` - ${bloques.empresa.detalle}`}
          </p>

          {bloques.declaraciones.length > 0 && (
            <ul className={propios.declaraciones}>
              {bloques.declaraciones.map((declaracion) => (
                <li key={declaracion}>{declaracion}</li>
              ))}
            </ul>
          )}

          {bloques.cierre && <p className={propios.cierre}>{bloques.cierre}</p>}
        </div>
      )}

      {/* Sin Form de antd a proposito: el estado de los checks es local y handleSubmit lo lee
          directo. El Form.Item de las otras bienvenidas envuelve un div, que antd no bindea, y el
          valor solo llega por el valuePropName del Checkbox; con dos checks esa indireccion es una
          fuente de bugs sin ninguna contrapartida. */}
      <div className={propios.aceptaciones}>
        <label className={propios.aceptacion}>
          <Checkbox checked={aceptaTerminos} onChange={(e) => setAceptaTerminos(e.target.checked)} />
          <span className={propios.textoAceptacion}>
            Acepto los{' '}
            <a
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                openTyC();
              }}
              style={{ fontWeight: 'bold', cursor: 'pointer', textDecoration: 'underline' }}
            >
              <Highlight>Términos y Condiciones</Highlight>
            </a>
          </span>
        </label>

        <label className={propios.aceptacion}>
          <Checkbox checked={aceptaDatos} onChange={(e) => setAceptaDatos(e.target.checked)} />
          <span className={propios.textoAceptacion}>Declaro que los datos y las declaraciones juradas de la empresa son correctos</span>
        </label>
      </div>

      <Button className={classes.btnInicio} type="primary" onClick={handleSubmit} loading={enviando}>
        Iniciar
      </Button>
    </div>
  );
};

export default OnboardingRepresentanteLegal;
